/**
 * Paso "Revisar ítems" del asistente de importación (spec 009, MedicalProtocols 031): estado de la selección y de las
 * elecciones del usuario, reducer puro y armado del cuerpo de la importación. Sin efectos: el hook `useImportSelection`
 * hace las llamadas y despacha las acciones.
 */
import {
  DEFAULT_SELECTED_STATUSES,
  ImportAssignment,
  ImportExcludedByStatus,
  ImportConflictReason,
  ImportDuplicateStatus,
  ImportItemAssignment,
  ImportItemError,
  ImportItemErrorField,
  ImportItemMedicalService,
  ImportItemServiceResolution,
  ImportItemSuggestion,
  ImportPreviewItem,
  ImportProviderFileConsumptionsRequest,
  MedicalServiceChoice,
  PREVIEW_ITEMS_DEFAULT_PAGE_SIZE,
  PreviewItemAmount,
  PreviewItemsFilter,
  ServiceResolutionOrigin,
  ServiceSourceKeyKind,
  VoucherHeader,
} from '../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { StatusTone } from '../theme/guideTokens';

// ---------------------------------------------------------------------------------------------
// Ítems
// ---------------------------------------------------------------------------------------------

const STATUS_BY_NAME: Record<string, ImportDuplicateStatus> = {
  new: ImportDuplicateStatus.New,
  copyofvoucher: ImportDuplicateStatus.CopyOfVoucher,
  possibleduplicate: ImportDuplicateStatus.PossibleDuplicate,
  possibleduplicateinfile: ImportDuplicateStatus.PossibleDuplicate,
  alreadyimportedthisfile: ImportDuplicateStatus.AlreadyImportedThisFile,
  alreadyimportedotherfile: ImportDuplicateStatus.AlreadyImportedOtherFile,
};

/** Estado de duplicado del ítem (número 1..5 o nombre del enum de MP); sin dato = Nuevo. */
export const duplicateStatusOf = (item: Pick<ImportPreviewItem, 'duplicateStatus'> | null | undefined): ImportDuplicateStatus => {
  const raw = item?.duplicateStatus;
  if (raw === null || raw === undefined || raw === '') return ImportDuplicateStatus.New;
  const numeric = Number(raw);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= 5) return numeric as ImportDuplicateStatus;
  return STATUS_BY_NAME[String(raw).replace(/[\s_-]+/g, '').toLowerCase()] ?? ImportDuplicateStatus.New;
};

/** Se puede marcar: `selectable` del servidor o, si falta, todo salvo "Ya importado de este archivo". */
export const isSelectable = (item: Pick<ImportPreviewItem, 'selectable' | 'duplicateStatus'>): boolean =>
  item.selectable ?? duplicateStatusOf(item) !== ImportDuplicateStatus.AlreadyImportedThisFile;

/** Ítems de la página con id (los de un backend anterior a 031 no lo traen y no se pueden seleccionar). */
export const itemsWithId = (items: ImportPreviewItem[] | null | undefined): Array<ImportPreviewItem & { id: string }> =>
  (items ?? []).filter((item): item is ImportPreviewItem & { id: string } => !!item?.id);

/** Ids seleccionables de una página. */
export const selectableIds = (items: ImportPreviewItem[] | null | undefined): string[] =>
  itemsWithId(items)
    .filter(isSelectable)
    .map((item) => item.id);

/** Estado de la casilla del encabezado para la página visible: marcada, mixta o vacía. */
export const pageSelectionState = (items: ImportPreviewItem[] | null | undefined, selected: ReadonlySet<string>): boolean | 'mixed' => {
  const ids = selectableIds(items);
  if (ids.length === 0) return false;
  const count = ids.filter((id) => selected.has(id)).length;
  if (count === 0) return false;
  return count === ids.length ? true : 'mixed';
};

// ---------------------------------------------------------------------------------------------
// Duplicados y comprobante (US3)
// ---------------------------------------------------------------------------------------------

/** Traductor mínimo (i18next `t`) para las funciones de texto. */
export type ReviewTranslate = (key: string, options?: Record<string, unknown>) => string;

/** Ícono del estado (el componente lo mapea a un ícono de Fluent). */
export type DuplicateStatusIcon = 'new' | 'copy' | 'possibleDuplicate' | 'alreadyImported' | 'importedOther';

export interface DuplicateStatusView {
  status: ImportDuplicateStatus;
  /** Tono de `StatusPill`. */
  tone: StatusTone;
  /** Símbolo de alto contraste (el estado nunca depende sólo del color). */
  symbol: string;
  icon: DuplicateStatusIcon;
  /** "Nuevo", "Copia (DUPLICADO)", "Posible duplicado", "Ya importado", "Importado en {archivo} el {fecha}". */
  label: string;
  /** Detalle secundario: "Igual a la fila 1". */
  detail?: string;
}

const STATUS_PRESENTATION: Record<ImportDuplicateStatus, { tone: StatusTone; symbol: string; icon: DuplicateStatusIcon }> = {
  [ImportDuplicateStatus.New]: { tone: 'success', symbol: '●', icon: 'new' },
  [ImportDuplicateStatus.CopyOfVoucher]: { tone: 'neutral', symbol: '⧉', icon: 'copy' },
  [ImportDuplicateStatus.PossibleDuplicate]: { tone: 'warning', symbol: '≈', icon: 'possibleDuplicate' },
  [ImportDuplicateStatus.AlreadyImportedThisFile]: { tone: 'info', symbol: '✓', icon: 'alreadyImported' },
  [ImportDuplicateStatus.AlreadyImportedOtherFile]: { tone: 'warning', symbol: '↺', icon: 'importedOther' },
};

/** Fecha corta (dd/mm/aaaa) de una fecha ISO (UTC); "" si no hay. */
const shortDate = (value: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

/** Presentación del estado de duplicado: tono, símbolo, ícono, texto y detalle (FR-009). */
export const duplicateStatusView = (
  item: Pick<ImportPreviewItem, 'duplicateStatus' | 'documentCopy' | 'duplicateOf'>,
  t: ReviewTranslate
): DuplicateStatusView => {
  const status = duplicateStatusOf(item);
  const base = { status, ...STATUS_PRESENTATION[status] };
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.status.${key}`, options);
  const sameRow = item.duplicateOf?.rowNumber ? tr('sameAs', { row: item.duplicateOf.rowNumber }) : undefined;
  switch (status) {
    case ImportDuplicateStatus.CopyOfVoucher: {
      const copy = item.documentCopy?.trim();
      return { ...base, label: copy ? tr('copyOf', { copy }) : tr('copy'), detail: sameRow };
    }
    case ImportDuplicateStatus.PossibleDuplicate:
      return { ...base, label: tr('possibleDuplicate'), detail: sameRow };
    case ImportDuplicateStatus.AlreadyImportedThisFile:
      return { ...base, label: tr('alreadyImported') };
    case ImportDuplicateStatus.AlreadyImportedOtherFile: {
      const file = item.duplicateOf?.fileName?.trim();
      const date = shortDate(item.duplicateOf?.importedAt);
      if (file && date) return { ...base, label: tr('importedOther', { file, date }) };
      if (file) return { ...base, label: tr('importedOtherFile', { file }) };
      return { ...base, label: tr('importedOtherUnknown') };
    }
    default:
      return { ...base, label: tr('new') };
  }
};

const VOUCHER_KINDS: Record<string, 'invoice' | 'debitNote' | 'creditNote' | 'receipt'> = {
  '1': 'invoice',
  '6': 'invoice',
  '11': 'invoice',
  '51': 'invoice',
  '2': 'debitNote',
  '7': 'debitNote',
  '12': 'debitNote',
  '52': 'debitNote',
  '3': 'creditNote',
  '8': 'creditNote',
  '13': 'creditNote',
  '53': 'creditNote',
  '4': 'receipt',
  '9': 'receipt',
  '15': 'receipt',
  '54': 'receipt',
};

/** Clase del comprobante por su código de ARCA ("01" = Factura). */
export const voucherKindOf = (voucherTypeCode: string | null | undefined): 'invoice' | 'debitNote' | 'creditNote' | 'receipt' | 'voucher' => {
  const digits = (voucherTypeCode ?? '').replace(/\D+/g, '');
  return VOUCHER_KINDS[String(Number(digits || 'NaN'))] ?? 'voucher';
};

/** "00003-00000509" (punto de venta a 5 y número a 8 dígitos); "" si falta alguno. */
export const voucherNumberLabel = (voucher: Pick<VoucherHeader, 'pointOfSale' | 'number'> | null | undefined): string => {
  if (!voucher?.pointOfSale || !voucher.number) return '';
  return `${String(voucher.pointOfSale).padStart(5, '0')}-${String(voucher.number).padStart(8, '0')}`;
};

/** Chip del comprobante: "Factura A 00003-00000509 · WILKINS GONZALO MATIAS · 3 copias" (FR-010); "" sin datos. */
export const voucherLabel = (voucher: VoucherHeader | null | undefined, t: ReviewTranslate): string => {
  if (!voucher) return '';
  const tr = (key: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.review.voucher.${key}`, options);
  const kind = voucherKindOf(voucher.voucherTypeCode);
  const title = [tr(`kinds.${kind}`), voucher.voucherType?.trim(), voucherNumberLabel(voucher)].filter(Boolean).join(' ');
  const hasIdentity = !!voucher.voucherType?.trim() || !!voucherNumberLabel(voucher) || kind !== 'voucher';
  const copies = (voucher.copies ?? []).filter((copy) => !!copy?.trim()).length;
  const parts = [hasIdentity ? title : '', voucher.issuer?.businessName?.trim() ?? ''];
  if (copies > 1) parts.push(tr('copies', { count: copies }));
  else if (copies === 1) parts.push(tr('copyOne'));
  return parts.filter(Boolean).join(' · ');
};

/** Fecha corta de `voucherAlreadyImported.importedAt` para el aviso. */
export const importedAtLabel = (value: string | null | undefined): string => shortDate(value);

// ---------------------------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------------------------

/** `data.itemErrors` del 400 agrupados por `itemId` (los generales, sin `itemId`, quedan fuera). */
export const itemErrorsById = (itemErrors: ImportItemError[] | null | undefined): Map<string, ImportItemError[]> => {
  const result = new Map<string, ImportItemError[]>();
  (itemErrors ?? []).forEach((error) => {
    if (!error?.itemId) return;
    const id = String(error.itemId);
    result.set(id, [...(result.get(id) ?? []), error]);
  });
  return result;
};

/** Motivo de un 409 de la importación (`data.reason`); un 409 con `JobId` sin motivo es ImportInProgress (MP 030). */
export const conflictReasonOf = (error: unknown): ImportConflictReason | undefined => {
  const typed = error as { ErrorCode?: number | string; Reason?: string; JobId?: string } | null | undefined;
  if (!typed || Number(typed.ErrorCode) !== 409) return undefined;
  if (typed.Reason === 'PreviewExpired' || typed.Reason === 'ImportInProgress' || typed.Reason === 'MappingConflict') return typed.Reason;
  return typed.JobId ? 'ImportInProgress' : undefined;
};

/** 404 de la API (vista previa vencida, reemplazada o ajena en `items` / `item-ids` / importación). */
export const isNotFoundError = (error: unknown): boolean => Number((error as { ErrorCode?: number } | null | undefined)?.ErrorCode) === 404;

/** La vista previa ya no sirve: 409 PreviewExpired o 404. */
export const isPreviewGoneError = (error: unknown): boolean => conflictReasonOf(error) === 'PreviewExpired' || isNotFoundError(error);

// ---------------------------------------------------------------------------------------------
// Filtros para "Aplicar a todos los iguales"
// ---------------------------------------------------------------------------------------------

/** Filtros de `item-ids` para los ítems con la misma sugerencia (sólo con `Matched`). */
export const sameSuggestionFilters = (item: Pick<ImportPreviewItem, 'suggestion'>): PreviewItemsFilter | undefined => {
  const suggestion = item.suggestion;
  if (suggestion?.status !== 'Matched' || !suggestion.protocolId) return undefined;
  const patient = suggestion.patientNumberInProtocol?.trim();
  return patient ? { suggestedProtocolId: suggestion.protocolId, suggestedPatientNumber: patient } : { suggestedProtocolId: suggestion.protocolId };
};

/** Filtros de `item-ids` para los ítems con la misma clave de servicio. */
export const sameServiceKeyFilters = (item: Pick<ImportPreviewItem, 'serviceResolution'>): PreviewItemsFilter | undefined => {
  const key = item.serviceResolution?.sourceKey?.trim();
  return key ? { serviceSourceKey: key } : undefined;
};

/**
 * Asignación que resulta de aplicar la sugerencia (sólo con `Matched` y protocolo): con `patientValid = false` se aplica
 * sólo el protocolo (el paciente no existe en el protocolo y la API respondería 400).
 */
export const suggestionAssignment = (suggestion: ImportItemSuggestion | null | undefined): ImportAssignment | undefined => {
  if (suggestion?.status !== 'Matched' || !suggestion.protocolId) return undefined;
  const patient = suggestion.patientValid === false ? null : suggestion.patientNumberInProtocol?.trim() || null;
  return { protocolId: suggestion.protocolId, patientNumberInProtocol: patient };
};

// ---------------------------------------------------------------------------------------------
// Estado de la revisión
// ---------------------------------------------------------------------------------------------

/** Asignación propia de una fila (US2): la sugerencia aplicada también es propia. */
export type ItemAssignmentOverride = { kind: 'own'; assignment: ImportAssignment; fromSuggestion?: boolean } | { kind: 'unassigned' };

/** Elección de servicio médico (US6); `label` = nombre para mostrar ("Elegido"). */
export type ItemServiceOverride = MedicalServiceChoice & { label?: string };

export interface ImportReviewFilters {
  statuses: ImportDuplicateStatus[];
  search: string;
}

export interface ImportReviewState {
  previewId: string;
  selected: Set<string>;
  /** Importes de las páginas cargadas y de cada respuesta de `item-ids` (sin importe = 0). */
  amountById: Map<string, number>;
  defaultAssignment?: ImportAssignment;
  itemAssignments: Map<string, ItemAssignmentOverride>;
  defaultService?: ItemServiceOverride;
  itemServices: Map<string, ItemServiceOverride>;
  /** "Recordar para este proveedor" (por defecto activa). */
  remember: boolean;
  /** `data.itemErrors` del último 400, por `itemId`. */
  itemErrors: Map<string, ImportItemError[]>;
  filters: ImportReviewFilters;
  page: { pageNumber: number; pageSize: number };
}

export type ImportReviewAction =
  | { type: 'init'; previewId: string; items: PreviewItemAmount[] }
  | { type: 'pageLoaded'; items: ImportPreviewItem[] }
  | { type: 'toggle'; itemId: string }
  | { type: 'selectPage'; items: ImportPreviewItem[] }
  | { type: 'deselectPage'; items: ImportPreviewItem[] }
  | { type: 'selectIds'; items: PreviewItemAmount[] }
  | { type: 'selectNone' }
  | { type: 'setDefaultAssignment'; assignment?: ImportAssignment }
  | { type: 'setItemAssignment'; itemId: string; override?: ItemAssignmentOverride }
  /** La misma asignación para varios ítems ("Aplicar a todos los iguales", con los ids de `item-ids`). */
  | { type: 'setItemAssignments'; itemIds: string[]; override?: ItemAssignmentOverride }
  | { type: 'applySuggestion'; items: ImportPreviewItem[] }
  | { type: 'setDefaultService'; choice?: ItemServiceOverride }
  | { type: 'setItemService'; itemId: string; choice?: ItemServiceOverride }
  /** La misma elección de servicio para varios ítems (misma clave de servicio, con los ids de `item-ids`). */
  | { type: 'setItemServices'; itemIds: string[]; choice?: ItemServiceOverride }
  | { type: 'setRemember'; value: boolean }
  | { type: 'setItemErrors'; itemErrors: ImportItemError[] }
  | { type: 'setFilters'; statuses?: ImportDuplicateStatus[]; search?: string }
  | { type: 'setPage'; pageNumber: number; pageSize?: number };

/** Estado inicial de una vista previa (sin selección: la inicial llega con `init`). */
export const initialImportReviewState = (previewId = ''): ImportReviewState => ({
  previewId,
  selected: new Set<string>(),
  amountById: new Map<string, number>(),
  itemAssignments: new Map<string, ItemAssignmentOverride>(),
  itemServices: new Map<string, ItemServiceOverride>(),
  remember: true,
  itemErrors: new Map<string, ImportItemError[]>(),
  filters: { statuses: [], search: '' },
  page: { pageNumber: 1, pageSize: PREVIEW_ITEMS_DEFAULT_PAGE_SIZE },
});

const amountOf = (value: number | null | undefined): number => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const withAmounts = (current: Map<string, number>, items: Array<{ id?: string; amount?: number | null }>): Map<string, number> => {
  const next = new Map(current);
  items.forEach((item) => {
    if (item.id) next.set(String(item.id), amountOf(item.amount));
  });
  return next;
};

const mapWith = <V>(current: Map<string, V>, key: string, value: V | undefined): Map<string, V> => {
  const next = new Map(current);
  if (value === undefined) next.delete(key);
  else next.set(key, value);
  return next;
};

/** Reducer puro de la revisión (selección, asignaciones, servicios, errores, filtros y página). */
export const importReviewReducer = (state: ImportReviewState, action: ImportReviewAction): ImportReviewState => {
  switch (action.type) {
    case 'init': {
      // Misma vista previa (llegó la selección inicial): conserva los importes ya leídos, los filtros y la página.
      const same = state.previewId === action.previewId;
      const fresh = initialImportReviewState(action.previewId);
      return {
        ...fresh,
        filters: same ? state.filters : fresh.filters,
        page: same ? state.page : fresh.page,
        selected: new Set(action.items.map((item) => String(item.id))),
        amountById: withAmounts(same ? state.amountById : fresh.amountById, action.items),
      };
    }
    case 'pageLoaded':
      return { ...state, amountById: withAmounts(state.amountById, itemsWithId(action.items)) };
    case 'toggle': {
      const selected = new Set(state.selected);
      if (selected.has(action.itemId)) selected.delete(action.itemId);
      else selected.add(action.itemId);
      return { ...state, selected };
    }
    case 'selectPage': {
      const selected = new Set(state.selected);
      selectableIds(action.items).forEach((id) => selected.add(id));
      return { ...state, selected, amountById: withAmounts(state.amountById, itemsWithId(action.items)) };
    }
    case 'deselectPage': {
      const selected = new Set(state.selected);
      itemsWithId(action.items).forEach((item) => selected.delete(item.id));
      return { ...state, selected };
    }
    case 'selectIds': {
      const selected = new Set(state.selected);
      action.items.forEach((item) => selected.add(String(item.id)));
      return { ...state, selected, amountById: withAmounts(state.amountById, action.items) };
    }
    case 'selectNone':
      return { ...state, selected: new Set<string>() };
    case 'setDefaultAssignment':
      return { ...state, defaultAssignment: action.assignment };
    case 'setItemAssignment':
      return { ...state, itemAssignments: mapWith(state.itemAssignments, action.itemId, action.override) };
    case 'setItemAssignments': {
      let itemAssignments = state.itemAssignments;
      action.itemIds.forEach((itemId) => {
        itemAssignments = mapWith(itemAssignments, String(itemId), action.override);
      });
      return { ...state, itemAssignments };
    }
    case 'applySuggestion': {
      const itemAssignments = new Map(state.itemAssignments);
      itemsWithId(action.items).forEach((item) => {
        const assignment = suggestionAssignment(item.suggestion);
        if (assignment) itemAssignments.set(item.id, { kind: 'own', fromSuggestion: true, assignment });
      });
      return { ...state, itemAssignments };
    }
    case 'setDefaultService':
      return { ...state, defaultService: action.choice };
    case 'setItemService':
      return { ...state, itemServices: mapWith(state.itemServices, action.itemId, action.choice) };
    case 'setItemServices': {
      let itemServices = state.itemServices;
      action.itemIds.forEach((itemId) => {
        itemServices = mapWith(itemServices, String(itemId), action.choice);
      });
      return { ...state, itemServices };
    }
    case 'setRemember':
      return { ...state, remember: action.value };
    case 'setItemErrors':
      return { ...state, itemErrors: itemErrorsById(action.itemErrors) };
    case 'setFilters':
      return {
        ...state,
        filters: { statuses: action.statuses ?? state.filters.statuses, search: action.search ?? state.filters.search },
        page: { ...state.page, pageNumber: 1 },
      };
    case 'setPage':
      return { ...state, page: { pageNumber: Math.max(1, action.pageNumber), pageSize: action.pageSize ?? state.page.pageSize } };
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------------------------
// Derivados
// ---------------------------------------------------------------------------------------------

/** Importe seleccionado: suma exacta de `amountById` sobre la selección (sin recorrer páginas). */
export const selectedAmount = (state: Pick<ImportReviewState, 'selected' | 'amountById'>): number => {
  let total = 0;
  state.selected.forEach((id) => {
    total += state.amountById.get(id) ?? 0;
  });
  return Math.round(total * 100) / 100;
};

/** Claves de `job.result.excludedByStatus`, en el orden de los estados. */
export const EXCLUDED_STATUS_KEYS: Array<keyof ImportExcludedByStatus> = [
  'new',
  'copyOfVoucher',
  'possibleDuplicate',
  'alreadyImportedThisFile',
  'alreadyImportedOtherFile',
];

/** Total de ítems excluidos por su clasificación. */
export const excludedByStatusTotal = (value: ImportExcludedByStatus | null | undefined): number =>
  EXCLUDED_STATUS_KEYS.reduce((total, key) => total + amountOf(value?.[key]), 0);

/** Detalle "Copias 6 · De otro archivo 1" de los estados con algún excluido ("" si no hay). */
export const excludedByStatusDetail = (
  value: ImportExcludedByStatus | null | undefined,
  label: (key: keyof ImportExcludedByStatus) => string
): string =>
  EXCLUDED_STATUS_KEYS.filter((key) => amountOf(value?.[key]) > 0)
    .map((key) => `${label(key)} ${amountOf(value?.[key])}`)
    .join(' · ');

/** Filtros de la tabla para `items` / `item-ids`. */
export const reviewFilterOf = (filters: ImportReviewFilters): PreviewItemsFilter => {
  const filter: PreviewItemsFilter = {};
  if (filters.statuses.length > 0) filter.duplicateStatus = [...filters.statuses].sort((a, b) => a - b);
  if (filters.search.trim()) filter.search = filters.search.trim();
  return filter;
};

/** Filtro de la selección inicial (estados 1 y 3, RF-011). */
export const defaultSelectionFilter = (): PreviewItemsFilter => ({ duplicateStatus: [...DEFAULT_SELECTED_STATUSES] });

const stripLabel = (choice: ItemServiceOverride): MedicalServiceChoice => {
  if ('medicalServiceId' in choice) return { medicalServiceId: choice.medicalServiceId };
  if ('create' in choice) return { create: { code: choice.create.code?.trim() || null, name: choice.create.name.trim() } };
  return { auto: true };
};

/**
 * Cuerpo de la importación con vista previa: `previewId` y `selectedItemIds` explícitos (nunca `selectAll`), y sólo si
 * hay elecciones, las asignaciones y los servicios de los ítems seleccionados. Sin `valueColumn` ni `mappingOverride`.
 */
export const buildReviewImportRequest = (state: ImportReviewState): ImportProviderFileConsumptionsRequest => {
  const selectedItemIds = Array.from(state.selected);
  const body: ImportProviderFileConsumptionsRequest = { previewId: state.previewId, selectedItemIds };
  if (state.defaultAssignment) body.defaultAssignment = state.defaultAssignment;
  const itemAssignments: ImportItemAssignment[] = [];
  state.itemAssignments.forEach((override, itemId) => {
    if (!state.selected.has(itemId)) return;
    itemAssignments.push(override.kind === 'unassigned' ? { itemId, unassigned: true } : { itemId, ...override.assignment });
  });
  if (itemAssignments.length > 0) body.itemAssignments = itemAssignments;
  if (state.defaultService) body.defaultMedicalService = stripLabel(state.defaultService);
  const itemMedicalServices: ImportItemMedicalService[] = [];
  state.itemServices.forEach((choice, itemId) => {
    if (state.selected.has(itemId)) itemMedicalServices.push({ itemId, ...stripLabel(choice) });
  });
  if (itemMedicalServices.length > 0) body.itemMedicalServices = itemMedicalServices;
  if (body.defaultMedicalService || itemMedicalServices.length > 0) body.rememberServiceMappings = state.remember;
  return body;
};

// ---------------------------------------------------------------------------------------------
// Asignación de protocolo y paciente (US2)
// ---------------------------------------------------------------------------------------------

/**
 * De dónde sale la asignación que se mostraría para el ítem: propia (elegida en la fila), sugerida (propia, aplicada desde
 * la sugerencia), general (la de la barra, sólo para los seleccionados), sin asignar (elegido explícitamente) o ninguna.
 */
export type AssignmentSource = 'own' | 'suggested' | 'general' | 'unassigned' | 'none';

export interface EffectiveAssignment {
  source: AssignmentSource;
  assignment?: ImportAssignment;
}

/** Asignación efectiva del ítem: la propia reemplaza a la general; la general sólo aplica a los seleccionados. */
export const effectiveAssignment = (
  state: Pick<ImportReviewState, 'selected' | 'defaultAssignment' | 'itemAssignments'>,
  itemId: string
): EffectiveAssignment => {
  const override = state.itemAssignments.get(itemId);
  if (override?.kind === 'unassigned') return { source: 'unassigned' };
  if (override?.kind === 'own') return { source: override.fromSuggestion ? 'suggested' : 'own', assignment: override.assignment };
  if (state.defaultAssignment && state.selected.has(itemId)) return { source: 'general', assignment: state.defaultAssignment };
  return { source: 'none' };
};

/** Campos de `itemErrors`, en el orden en que se listan. */
export const ITEM_ERROR_FIELDS: ImportItemErrorField[] = ['protocol', 'patient', 'medicalService', 'selection'];

/** Fila del aviso "Hay N ítems con errores" → "Ver". */
export interface ItemErrorRow {
  itemId: string;
  rowNumber?: number | null;
  field: ImportItemErrorField;
  message: string;
}

const fieldOrder = (field: ImportItemErrorField): number => {
  const index = ITEM_ERROR_FIELDS.indexOf(field);
  return index < 0 ? ITEM_ERROR_FIELDS.length : index;
};

/** Errores por ítem ordenados por fila y campo (para listarlos con "Ver"). */
export const itemErrorRows = (itemErrors: ReadonlyMap<string, ImportItemError[]>): ItemErrorRow[] => {
  const rows: ItemErrorRow[] = [];
  itemErrors.forEach((errors, itemId) =>
    errors.forEach((error) => rows.push({ itemId, rowNumber: error.rowNumber, field: error.field, message: error.message }))
  );
  const rowOf = (row: ItemErrorRow) => Number(row.rowNumber ?? Number.MAX_SAFE_INTEGER);
  return rows.sort((a, b) => rowOf(a) - rowOf(b) || fieldOrder(a.field) - fieldOrder(b.field));
};

/** Errores de la asignación o del servicio generales (`itemId` null, AJ-06): sólo van al banner. */
export const generalItemErrors = (itemErrors: ImportItemError[] | null | undefined): ImportItemError[] =>
  (itemErrors ?? []).filter((error) => !!error && !error.itemId);

// ---------------------------------------------------------------------------------------------
// Servicio médico por ítem (US6)
// ---------------------------------------------------------------------------------------------

const SOURCE_KEY_KINDS: ServiceSourceKeyKind[] = ['TemplateService', 'Code', 'Name'];

/** Tipo de clave de servicio (nombre o número 1..3 del enum de MP); undefined si no se reconoce. */
export const serviceSourceKeyKindOf = (value: unknown): ServiceSourceKeyKind | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const numeric = Number(value);
  if (Number.isInteger(numeric) && numeric >= 1 && numeric <= SOURCE_KEY_KINDS.length) return SOURCE_KEY_KINDS[numeric - 1];
  const text = String(value).replace(/[\s_-]+/g, '').toLowerCase();
  return SOURCE_KEY_KINDS.find((kind) => kind.toLowerCase() === text);
};

/**
 * Origen del servicio que se usaría: los del servidor (`serviceResolution.origin`), `Chosen` (servicio existente elegido
 * en esta importación; lo calcula el front), `CreateOnRequest` (servicio nuevo pedido por el usuario) o `Unknown`
 * (sin `serviceResolution`: MedicalAgreements no respondió, AJ-11).
 */
export type EffectiveServiceOrigin = ServiceResolutionOrigin | 'Chosen' | 'CreateOnRequest' | 'Unknown';

export interface EffectiveService {
  origin: EffectiveServiceOrigin;
  /** De dónde sale la elección: la fila, la barra (general) o la resolución automática del servidor. */
  source: 'item' | 'general' | 'server';
  name?: string | null;
  code?: string | null;
  medicalServiceId?: number | null;
  /** Sólo con la resolución del servidor. */
  variableDescriptionWarning?: boolean;
  serviceInactive?: boolean;
}

const fromServer = (resolution: ImportItemServiceResolution | null | undefined): EffectiveService => {
  if (!resolution) return { origin: 'Unknown', source: 'server' };
  return {
    origin: resolution.origin,
    source: 'server',
    name: resolution.name,
    code: resolution.code,
    medicalServiceId: resolution.medicalServiceId,
    variableDescriptionWarning: !!resolution.variableDescriptionWarning,
    serviceInactive: !!resolution.serviceInactive,
  };
};

/**
 * Servicio efectivo del ítem: la elección de la fila reemplaza a la general (que sólo aplica a los seleccionados);
 * `{ auto: true }` o sin elección = la resolución automática del servidor.
 */
export const effectiveService = (
  state: Pick<ImportReviewState, 'selected' | 'defaultService' | 'itemServices'>,
  item: Pick<ImportPreviewItem, 'id' | 'serviceResolution'>
): EffectiveService => {
  const id = item.id ? String(item.id) : undefined;
  const own = id ? state.itemServices.get(id) : undefined;
  const general = id && state.selected.has(id) ? state.defaultService : undefined;
  const choice = own ?? general;
  const source: EffectiveService['source'] = own ? 'item' : 'general';
  if (!choice || 'auto' in choice) return fromServer(item.serviceResolution);
  if ('medicalServiceId' in choice) return { origin: 'Chosen', source, medicalServiceId: choice.medicalServiceId, name: choice.label ?? null };
  return { origin: 'CreateOnRequest', source, name: choice.create.name, code: choice.create.code ?? null };
};

/**
 * "Volver a automático" en una fila: `{ auto: true }` si hay una elección general (para que no la herede) o sin entrada
 * (undefined) si no la hay.
 */
export const autoServiceChoice = (state: Pick<ImportReviewState, 'defaultService'>): ItemServiceOverride | undefined =>
  state.defaultService ? { auto: true } : undefined;

/** La fila usa una elección del usuario (propia o heredada de la general) en vez de la resolución automática. */
export const hasServiceChoice = (state: Pick<ImportReviewState, 'selected' | 'defaultService' | 'itemServices'>, itemId: string): boolean =>
  effectiveService(state, { id: itemId }).source !== 'server';

/** Límites de "Crear servicio nuevo" (MP 031 §2). */
export const SERVICE_CODE_MAX_LENGTH = 50;
export const SERVICE_NAME_MAX_LENGTH = 300;

export type ServiceCreateError = 'nameRequired' | 'nameTooLong' | 'codeTooLong';

/** Validación de "Crear servicio nuevo": nombre de 1 a 300 y código opcional de hasta 50 (recortados). */
export const validateServiceCreate = (values: {
  code?: string | null;
  name?: string | null;
}): { code?: ServiceCreateError; name?: ServiceCreateError } => {
  const errors: { code?: ServiceCreateError; name?: ServiceCreateError } = {};
  const name = (values.name ?? '').trim();
  const code = (values.code ?? '').trim();
  if (!name) errors.name = 'nameRequired';
  else if (name.length > SERVICE_NAME_MAX_LENGTH) errors.name = 'nameTooLong';
  if (code.length > SERVICE_CODE_MAX_LENGTH) errors.code = 'codeTooLong';
  return errors;
};
